import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { OwnerStatementSummariesTable } from './list';
import {
  OwnerStatementSummariesDetail,
  OwnerStatementSummariesDetailContextProvider,
  type OwnerStatementSummariesJournalEntriesArgs,
  type OwnerStatementsModalFilter,
} from './detail';
import { day } from '@vrplatform/utils';
import { isUUID } from '@vrplatform/utils';
import { TableStoreProvider } from '../../../stores';

type MonthRange = `${string}...${string}`;

export const OwnerStatementSummariesRoutes = ({
  onOpenJournalEntries,
  onOpenListing,
  onOpenPayout,
  renderDetailShell,
  renderListShell,
  renderOwnerStatementsTable,
}: {
  onOpenJournalEntries?: (
    args: OwnerStatementSummariesJournalEntriesArgs
  ) => void;
  onOpenListing?: (listingId: string) => void;
  onOpenPayout?: (payoutId: string) => void;
  renderDetailShell?: (args: {
    content: React.ReactNode;
    monthRange: MonthRange;
  }) => React.ReactNode;
  renderListShell?: (content: React.ReactNode) => React.ReactNode;
  renderOwnerStatementsTable?: (
    modalFilter: OwnerStatementsModalFilter | undefined
  ) => React.ReactNode;
}) => {
  const defaultMonthRange =
    `${day().startOf('year').yyyymmdd()}...${day().endOf('year').yyyymmdd()}` satisfies `${string}...${string}`;

  return (
    <Routes>
      <Route path="*">
        <Route
          index
          element={<Navigate to={`list/${defaultMonthRange}`} replace />}
        />

        <Route
          path="list"
          element={<Navigate to={`${defaultMonthRange}`} replace />}
        />

        <Route
          path="list/:monthRange"
          element={
            <List
              defaultMonthRange={defaultMonthRange}
              renderListShell={renderListShell}
            />
          }
        />

        <Route
          path=":contactId/:monthRange"
          element={
            <Detail
              defaultMonthRange={defaultMonthRange}
              onOpenJournalEntries={onOpenJournalEntries}
              onOpenListing={onOpenListing}
              onOpenPayout={onOpenPayout}
              renderDetailShell={renderDetailShell}
              renderOwnerStatementsTable={renderOwnerStatementsTable}
            />
          }
        />
      </Route>
    </Routes>
  );
};

const List = ({
  defaultMonthRange,
  renderListShell,
}: {
  defaultMonthRange: `${string}...${string}`;
  renderListShell?: (content: React.ReactNode) => React.ReactNode;
}) => {
  const { monthRange: initial } = useParams<{
    monthRange: `${string}...${string}`;
  }>();

  const monthRange = initial || defaultMonthRange;
  const content = <OwnerStatementSummariesTable />;
  const body = renderListShell ? renderListShell(content) : content;

  return (
    <OwnerStatementSummariesDetailContextProvider
      contactId={undefined}
      monthRange={monthRange}
    >
      <TableStoreProvider filterType="state" key={monthRange}>
        {body}
      </TableStoreProvider>
    </OwnerStatementSummariesDetailContextProvider>
  );
};

const Detail = ({
  defaultMonthRange,
  onOpenJournalEntries,
  onOpenListing,
  onOpenPayout,
  renderDetailShell,
  renderOwnerStatementsTable,
}: {
  defaultMonthRange: MonthRange;
  onOpenJournalEntries?: (
    args: OwnerStatementSummariesJournalEntriesArgs
  ) => void;
  onOpenListing?: (listingId: string) => void;
  onOpenPayout?: (payoutId: string) => void;
  renderDetailShell?: (args: {
    content: React.ReactNode;
    monthRange: MonthRange;
  }) => React.ReactNode;
  renderOwnerStatementsTable?: (
    modalFilter: OwnerStatementsModalFilter | undefined
  ) => React.ReactNode;
}) => {
  const { contactId, monthRange } = useParams<{
    contactId: string;
    monthRange: string;
  }>();

  const resolvedMonthRange = (monthRange || defaultMonthRange) as MonthRange;

  if (!contactId || !isUUID(contactId)) {
    return (
      <Navigate
        to={`/reports/statement-summaries/list/${resolvedMonthRange}`}
        replace
      />
    );
  }

  const content = (
    <OwnerStatementSummariesDetail
      onOpenJournalEntries={onOpenJournalEntries}
      onOpenListing={onOpenListing}
      onOpenPayout={onOpenPayout}
      renderOwnerStatementsTable={renderOwnerStatementsTable}
    />
  );

  return (
    <OwnerStatementSummariesDetailContextProvider
      contactId={contactId}
      monthRange={resolvedMonthRange}
    >
      {renderDetailShell
        ? renderDetailShell({
            content,
            monthRange: resolvedMonthRange,
          })
        : content}
    </OwnerStatementSummariesDetailContextProvider>
  );
};
